import React, { useState } from "react";
import { Search, MapPin, Star, CheckCircle2, HeartHandshake, ShieldCheck, Sparkles, CalendarCheck, Navigation, LocateFixed, Share2 } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccCard, ccBtnPink, ccBtnBlue, ccBtnGhost, Stars, Avatar, EmptyState, CCModal, ModalHeader } from "./ui";
import { SERVICE_TYPES, FEATURED_DISCLAIMER } from "./careData";
import { CAREGIVER_GEO } from "../modules/opsData";
import { haversineMiles, getDeviceLocation } from "../lib/location";
import ContactButtons from "../lib/ContactButtons";
import AdSlot from "../AdSlot";
import SocialLinks from "../lib/SocialLinks";

// ---------- Care Connections Home ----------
export const CareHome = ({ ctx }) => {
  const featured = ctx.caregivers.filter((c) => c.featured && c.status === "active");
  return (
    <div className="space-y-8" data-testid="cc-home">
      <div className="p-8 bg-gradient-to-r from-[#FCE6E9] to-[#E6F2FA] border border-[#F8C8DC] rounded-3xl animate-fade-up dark:border-[#374457] dark:from-[#273244] dark:to-[#232D3D]">
        <h2 className="text-3xl sm:text-4xl font-black font-heading text-[#374151] dark:text-slate-100 tracking-tight">
          More than daycare. <span className="text-[#E5A0BC]">A connection to care.</span>
        </h2>
        <p className="text-sm text-[#6B7280] mt-3 max-w-2xl font-medium dark:text-slate-400">
          Find experienced caregivers, respite workers, personal caregivers, babysitters and other care professionals
          available to help families when they need additional support.
        </p>
        <div className="flex gap-3 mt-6 flex-wrap">
          <button onClick={() => ctx.go("find")} data-testid="cc-find-caregiver-btn" className={ccBtnPink}>
            <Search className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Find a Caregiver
          </button>
          <button onClick={() => ctx.go("caregiving")} data-testid="cc-become-caregiver-btn" className={ccBtnBlue}>
            Become a Caregiver
          </button>
          <button onClick={() => ctx.go("requests")} data-testid="cc-my-requests-btn" className={ccBtnGhost}>
            My Care Requests
          </button>
          <button onClick={() => ctx.go("connections")} data-testid="cc-my-caregivers-btn" className={ccBtnGhost}>
            My Caregivers
          </button>
        </div>
      </div>

      <AdSlot placement="care" className="max-w-md" />

      {featured.length > 0 && (
        <div data-testid="cc-featured-section">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-[#F5C445]" />
            <h3 className="text-base font-extrabold font-heading text-[#374151] dark:text-slate-100">Featured Caregivers</h3>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mb-4">{FEATURED_DISCLAIMER}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {featured.map((c) => (
              <CaregiverCard key={c.id} c={c} ctx={ctx} featured />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Caregiver Card (shared) ----------
export const CaregiverCard = ({ c, ctx, featured = false }) => (
  <div
    className={`${ccCard} p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200`}
    data-testid={`cc-caregiver-card-${c.id}`}
  >
    <div className="flex items-start gap-4">
      <Avatar src={c.photo} name={c.name} size="w-14 h-14" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-extrabold text-[#374151] font-heading dark:text-slate-100">{c.name}</p>
          {c.available ? (
            <span className="flex items-center gap-1 text-[10px] font-bold text-[#2F6031]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7CC98F]" /> Available
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[10px] font-bold text-[#9CA3AF]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D1D5DB]" /> Booked
            </span>
          )}
          {featured && (
            <span className="px-2 py-0.5 bg-[#FFF2D1] border border-[#F5E3B3] text-[#7A5A17] text-[9px] font-extrabold rounded-full uppercase">
              Featured
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <Star className="w-3.5 h-3.5 text-[#F5C445] fill-[#F5C445]" />
          <span className="text-xs font-bold text-[#374151] dark:text-slate-200">{c.rating > 0 ? c.rating : "New"}</span>
          {c.reviewCount > 0 && <span className="text-[11px] text-[#9CA3AF]">({c.reviewCount} reviews)</span>}
        </div>
        <p className="text-[11px] text-[#6B7280] mt-1 truncate dark:text-slate-400">{c.services.slice(0, 3).join(" • ")}</p>
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {c.credentials
            .filter((cr) => cr.verified && ["CPR", "First Aid", "Background Check", "CNA"].includes(cr.type))
            .map((cr) => (
              <span key={cr.type} className="px-2 py-0.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-[9px] font-bold rounded-full">
                {cr.type} ✓
              </span>
            ))}
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-2">
          {c.yearsExperience} years experience · ${c.hourlyRate}/hr · {c.distanceMiles} miles away
        </p>
      </div>
    </div>
    <div className="flex gap-2 mt-4">
      <button
        onClick={() => ctx.viewCaregiver(c.id)}
        data-testid={`cc-view-profile-${c.id}`}
        className="flex-1 px-4 py-2 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold transition-all duration-200"
      >
        View Profile
      </button>
      <button
        onClick={() => ctx.openMessages(c.id)}
        data-testid={`cc-message-${c.id}`}
        className="flex-1 px-4 py-2 bg-white border border-[#B3E0F2] hover:bg-[#E6F2FA] text-[#3D7E9E] rounded-full text-xs font-bold transition-all duration-200 dark:bg-[#273244]"
      >
        Message
      </button>
    </div>
  </div>
);

// ---------- Find Care ----------
export const FindCare = ({ ctx }) => {
  const prev = ctx.searchCriteria || {};
  const [services, setServices] = useState(prev.services || []);
  const [date, setDate] = useState(prev.date || "");
  const [startTime, setStartTime] = useState(prev.startTime || "");
  const [endTime, setEndTime] = useState(prev.endTime || "");
  const [zip, setZip] = useState(prev.zip || "");
  const [radius, setRadius] = useState(String(prev.radius || "15"));
  const [preference, setPreference] = useState(prev.preference || "No preference");
  const [nearMe, setNearMe] = useState(prev.nearMe || null);
  const [locating, setLocating] = useState(false);

  const useMyLocation = async () => {
    setLocating(true);
    try {
      const loc = await getDeviceLocation();
      setNearMe(loc);
      setZip("Current location");
      toast.success("Location set — results will be sorted nearest first.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLocating(false);
    }
  };

  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = (e) => {
    e.preventDefault();
    ctx.runSearch({ services, date, startTime, endTime, zip, radius: parseInt(radius), preference, nearMe });
  };

  return (
    <div className={`${ccCard} p-8 max-w-3xl animate-fade-up`} data-testid="cc-find-form">
      <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100 tracking-tight">Find a Caregiver</h2>
      <p className="text-xs text-[#9CA3AF] mt-1 mb-6 font-medium">Tell us what you need — we’ll match you with available caregivers.</p>
      <form onSubmit={submit} className="space-y-6">
        <div>
          <label className={ccLabel}>What type of help do you need?</label>
          <div className="flex gap-2 flex-wrap" data-testid="cc-service-chips">
            {SERVICE_TYPES.map((s, i) => (
              <button
                type="button"
                key={s}
                onClick={() => toggleService(s)}
                data-testid={`cc-service-chip-${i}`}
                className={`px-3.5 py-2 rounded-full text-xs font-bold border transition-all duration-200 ${
                  services.includes(s)
                    ? "bg-[#F8C8DC] border-[#EAAFC5] text-[#374151] shadow-sm"
                    : "bg-[#FFF9F2] border-[#FCE6E9] text-[#6B7280] hover:border-[#F8C8DC] dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={ccLabel}>When?</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={ccInput} data-testid="cc-find-date" />
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={ccInput} data-testid="cc-find-start" />
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={ccInput} data-testid="cc-find-end" />
          </div>
        </div>
        <div>
          <label className={ccLabel}>Where?</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="ZIP code or city"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              className={ccInput}
              data-testid="cc-find-zip"
            />
            <select value={radius} onChange={(e) => setRadius(e.target.value)} className={ccInput} data-testid="cc-find-radius">
              {["5", "10", "15", "25", "50"].map((r) => (
                <option key={r} value={r}>{`Within ${r} miles`}</option>
              ))}
            </select>
          </div>
          <button type="button" onClick={useMyLocation} disabled={locating} data-testid="cc-find-near-me" className="mt-3 flex items-center gap-1.5 px-4 py-2 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-xs font-bold transition-all disabled:opacity-50">
            <LocateFixed className="w-3.5 h-3.5" /> {locating ? "Locating…" : nearMe ? "Using current location ✓" : "Caregivers Near Me"}
          </button>
        </div>
        <div>
          <label className={ccLabel}>Preferences</label>
          <select value={preference} onChange={(e) => setPreference(e.target.value)} className={ccInput} data-testid="cc-find-preference">
            <option>No preference</option>
            <option>Any caregiver</option>
            <option>Female</option>
            <option>Male</option>
          </select>
        </div>
        <button type="submit" data-testid="cc-find-submit" className={`${ccBtnPink} w-full sm:w-auto`}>
          <Search className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Find Caregivers
        </button>
      </form>
    </div>
  );
};

// ---------- Search Results ----------
export const SearchResults = ({ ctx }) => {
  const crit = ctx.searchCriteria || { services: [], radius: 50 };
  const results = ctx.caregivers
    .map((c) => {
      const g = CAREGIVER_GEO[c.id];
      const dist = crit.nearMe && g ? haversineMiles(crit.nearMe.lat, crit.nearMe.lng, g.lat, g.lng) : c.distanceMiles;
      return { ...c, distanceMiles: Math.round(dist * 10) / 10 };
    })
    .filter(
      (c) =>
        c.status === "active" &&
        c.distanceMiles <= (crit.radius || 50) &&
        (crit.services.length === 0 || c.services.some((s) => crit.services.includes(s)))
    )
    .sort((a, b) => a.distanceMiles - b.distanceMiles);
  const dayName = crit.date ? new Date(`${crit.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "long" }) : null;
  const matchScore = (c) => {
    let s = c.rating || 0;
    s += (crit.services || []).filter((sv) => c.services.includes(sv)).length * 3;
    if (dayName && c.availability?.[dayName]) s += 2;
    if (c.available) s += 1;
    s -= (c.distanceMiles || 0) / 10;
    return s;
  };
  const suggested =
    results.length > 0 && ((crit.services || []).length > 0 || crit.date)
      ? [...results].sort((a, b) => matchScore(b) - matchScore(a)).slice(0, 3)
      : [];
  const suggestedIds = new Set(suggested.map((c) => c.id));
  const featured = results.filter((c) => c.featured && !suggestedIds.has(c.id));
  const regular = results.filter((c) => !c.featured && !suggestedIds.has(c.id));

  return (
    <div className="space-y-6" data-testid="cc-results">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100" data-testid="cc-results-count">
            {results.length} Caregivers Available
          </h2>
          <p className="text-xs text-[#9CA3AF] font-medium mt-1">
            {crit.services.length > 0 ? crit.services.join(" • ") : "All services"} · within {crit.radius || 50} miles
            {crit.zip ? ` of ${crit.zip}` : ""}
          </p>
        </div>
        <button onClick={() => ctx.go("find")} data-testid="cc-edit-search-btn" className={ccBtnGhost}>
          Edit Search
        </button>
      </div>

      {suggested.length > 0 && (
        <div data-testid="cc-suggested">
          <p className="text-[11px] text-[#9CA3AF] mb-3 flex items-center gap-1.5 font-bold">
            <Sparkles className="w-3.5 h-3.5 text-[#E5A0BC]" /> Suggested for you — matched on your services
            {dayName ? `, ${dayName} availability` : ""}, ratings, and distance
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {suggested.map((c) => (
              <CaregiverCard key={c.id} c={c} ctx={ctx} />
            ))}
          </div>
        </div>
      )}

      {featured.length > 0 && (
        <div>
          <p className="text-[11px] text-[#9CA3AF] mb-3 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#F5C445]" /> Featured — {FEATURED_DISCLAIMER}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {featured.map((c) => (
              <CaregiverCard key={c.id} c={c} ctx={ctx} featured />
            ))}
          </div>
        </div>
      )}

      {results.length === 0 ? (
        <EmptyState text="No caregivers match your search yet. Try widening the radius or removing a service filter." testId="cc-results-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {regular.map((c) => (
            <CaregiverCard key={c.id} c={c} ctx={ctx} />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Caregiver Profile ----------
export const CaregiverProfile = ({ ctx }) => {
  const c = ctx.caregivers.find((x) => x.id === ctx.selectedCaregiverId);
  if (!c) return <EmptyState text="Caregiver not found." testId="cc-profile-missing" />;
  const cReviews = ctx.reviews.filter((r) => r.caregiverId === c.id);
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="cc-profile">
      <div className={`${ccCard} p-8 animate-fade-up`}>
        <div className="flex items-start gap-5 flex-wrap">
          <Avatar src={c.photo} name={c.name} size="w-24 h-24" />
          <div className="flex-1 min-w-[220px]">
            <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100">{c.name}</h2>
            <p className="text-sm text-[#6B7280] font-medium dark:text-slate-400">{c.title}</p>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <Stars rating={c.rating} />
              <span className="text-xs font-bold text-[#374151] dark:text-slate-200">{c.rating > 0 ? c.rating : "New"}</span>
              <span className="text-xs text-[#9CA3AF]">{c.reviewCount} reviews</span>
              {c.available && (
                <span className="flex items-center gap-1 text-[11px] font-bold text-[#2F6031]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7CC98F]" /> Available for Care Connections
                </span>
              )}
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              <button onClick={() => ctx.openRequestModal(c.id)} data-testid="cc-request-care-btn" className={ccBtnPink}>
                <CalendarCheck className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Request Care
              </button>
              <button onClick={() => ctx.openMessages(c.id)} data-testid="cc-message-caregiver-btn" className={ccBtnBlue}>
                Message Caregiver
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/share/profile/${c.id}`);
                  toast.success("Share link copied — it renders a rich preview card when posted anywhere.");
                }}
                data-testid="cc-share-profile-btn"
                className="px-5 py-2.5 bg-[#E2F4E1] hover:bg-[#C4E5C2] border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-bold transition-colors flex items-center gap-1.5"
              >
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
            </div>
            <div className="mt-4 max-w-xs">
              <ContactButtons phone={c.phone} email={c.email} onMessage={() => ctx.openMessages(c.id)} testId="cc-profile-contact" />
            </div>
            {c.socials && Object.values(c.socials).some(Boolean) && (
              <div className="mt-3">
                <SocialLinks links={c.socials} testId="cc-profile-social" />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`${ccCard} p-6`}>
          <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-3">About Me</h3>
          <p className="text-sm text-[#6B7280] leading-relaxed dark:text-slate-300">{c.bio}</p>
          <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mt-6 mb-3">Services</h3>
          <div className="flex gap-1.5 flex-wrap" data-testid="cc-profile-services">
            {c.services.map((s) => (
              <span key={s} className="px-2.5 py-1 bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-[11px] font-bold rounded-full flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> {s}
              </span>
            ))}
          </div>
          <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mt-6 mb-2">Experience</h3>
          <p className="text-sm text-[#374151] font-bold dark:text-slate-200">{c.yearsExperience} years</p>
        </div>

        <div className={`${ccCard} p-6`}>
          <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-3 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#5CB3DC]" /> Credentials
          </h3>
          <div className="space-y-2" data-testid="cc-profile-credentials">
            {c.credentials.map((cr) => (
              <div key={cr.type} className="flex items-center gap-2 text-xs font-semibold">
                <span className={`w-2 h-2 rounded-full ${cr.verified ? "bg-[#7CC98F]" : "bg-[#D1D5DB]"}`} />
                <span className={cr.verified ? "text-[#374151] dark:text-slate-200" : "text-[#9CA3AF]"}>
                  {cr.type} {cr.verified ? "Verified" : "Not verified"}
                </span>
              </div>
            ))}
          </div>
          <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mt-6 mb-3">Availability</h3>
          <div className="space-y-1.5 text-xs">
            {days.map((d) =>
              c.availability[d] ? (
                <div key={d} className="flex justify-between">
                  <span className="text-[#9CA3AF] font-semibold">{d}</span>
                  <span className="text-[#374151] font-bold dark:text-slate-200">{c.availability[d]}</span>
                </div>
              ) : null
            )}
          </div>
          <div className="mt-6 pt-4 border-t border-[#FCE6E9] dark:border-[#374457] space-y-2 text-xs">
            <p className="flex items-center gap-1.5 text-[#6B7280] font-semibold dark:text-slate-400">
              <MapPin className="w-3.5 h-3.5 text-[#E5A0BC]" /> {c.city} · within {c.radiusMiles} miles
            </p>
            <p className="text-[#374151] font-extrabold text-sm dark:text-slate-100">
              ${c.hourlyRate}–${c.rateMax}/hour
            </p>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${
                CAREGIVER_GEO[c.id] ? `${CAREGIVER_GEO[c.id].lat},${CAREGIVER_GEO[c.id].lng}` : encodeURIComponent(c.city)
              }`}
              target="_blank"
              rel="noreferrer"
              data-testid="profile-directions-btn"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-[#E2F4E1] hover:bg-[#D2EBD1] text-[#2F6031] rounded-full text-[11px] font-bold transition-colors"
            >
              <Navigation className="w-3.5 h-3.5" /> Take Me To Location
            </a>
          </div>
        </div>
      </div>

      <div className={`${ccCard} p-6`} data-testid="cc-profile-reviews">
        <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-4">Reviews</h3>
        {cReviews.length === 0 ? (
          <p className="text-xs text-[#9CA3AF]">No reviews yet.</p>
        ) : (
          <div className="space-y-4">
            {cReviews.map((r) => (
              <div key={r.id} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl dark:bg-[#273244] dark:border-[#374457]" data-testid={`cc-review-${r.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-xs font-extrabold text-[#374151] dark:text-slate-100">{r.reviewer}</span>
                  <Stars rating={r.rating} size="w-3.5 h-3.5" />
                </div>
                <p className="text-sm text-[#6B7280] mt-2 dark:text-slate-300">“{r.text}”</p>
                <p className="text-[10px] text-[#9CA3AF] mt-1.5">{r.date}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Request Care Modal ----------
export const RequestCareModal = ({ ctx, caregiver, onClose }) => {
  const [service, setService] = useState(caregiver?.services[0] || SERVICE_TYPES[0]);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [notes, setNotes] = useState("");
  if (!caregiver) return null;

  const fmtTime = (t) => {
    if (!t) return t;
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  const submit = (e) => {
    e.preventDefault();
    ctx.sendRequest(caregiver.id, {
      service,
      date,
      startTime: fmtTime(startTime),
      endTime: fmtTime(endTime),
      location: `${caregiver.distanceMiles} miles from you`,
      notes,
    });
  };

  return (
    <CCModal onClose={onClose} testId="cc-request-modal">
      <ModalHeader title={`Request Care — ${caregiver.name}`} icon={HeartHandshake} onClose={onClose} closeTestId="cc-request-close" />
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={ccLabel}>Service</label>
          <select value={service} onChange={(e) => setService(e.target.value)} className={ccInput} data-testid="cc-request-service">
            {caregiver.services.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={ccLabel}>Date</label>
          <input required type="date" value={date} onChange={(e) => setDate(e.target.value)} className={ccInput} data-testid="cc-request-date" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={ccLabel}>Start Time</label>
            <input required type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={ccInput} data-testid="cc-request-start" />
          </div>
          <div>
            <label className={ccLabel}>End Time</label>
            <input required type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={ccInput} data-testid="cc-request-end" />
          </div>
        </div>
        <div>
          <label className={ccLabel}>Additional Information</label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={ccInput}
            placeholder="Anything the caregiver should know..."
            data-testid="cc-request-notes"
          />
        </div>
        <div className="pt-4 flex justify-end gap-3 border-t border-[#FCE6E9] dark:border-[#374457]">
          <button type="button" onClick={onClose} className={ccBtnGhost} data-testid="cc-request-cancel">
            Cancel
          </button>
          <button type="submit" className={ccBtnPink} data-testid="cc-request-submit">
            Send Request
          </button>
        </div>
      </form>
    </CCModal>
  );
};
